import { DragEvent, ReactNode, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TileIconBadge from './tile-icon-badge';
import {
  CommandDashboardTileDefinition,
  FULL_WIDTH_TILE_COLUMNS,
  TILE_DRAG_HANDLE_ATTRIBUTE,
  TILE_DRAG_MIME_TYPE,
  tileSizeForColumns,
} from './tile-config';
import {
  TileRefreshContext,
  TileRefreshRegistration,
} from './use-tile-refresh';
import { TileLinkStateContext } from './use-tile-link-state';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MoreVert from '@mui/icons-material/MoreVert';
import Refresh from '@mui/icons-material/Refresh';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';

interface CommandDashboardTileProps {
  tile: CommandDashboardTileDefinition;
  onRemove: (key: CommandDashboardTileDefinition['key']) => void;
  /**
   * Grid columns this tile spans, which picks the size (and so the height) it
   * is drawn at. Defaults to a full-width row for anything rendering the shell
   * outside the grid.
   */
  columns?: number;
  /** Makes the header a drag handle for moving the tile around the grid. */
  onDragStart?: () => void;
  onDragEnd?: () => void;
  /**
   * Shell tests (and anything that is not a registered body) can still pass
   * Refresh directly. Live tile bodies use `useTileRefresh` instead; props win
   * when both are present.
   */
  onRefresh?: () => void;
  isRefreshing?: boolean;
  // When set, the Refresh action is disabled and shows this text as a tooltip (eg. explaining a
  // lost permission) instead of just going quietly non-interactive.
  refreshDisabledReason?: string;
  /**
   * Extra router state carried through when navigating to `tile.link` (eg. a preset filter for
   * the destination page). Opaque to this component - the destination decides its shape. Shell
   * tests can still pass it directly; live tile bodies use `useTileLinkState` instead, the same
   * way refresh works - props win when both are present.
   */
  linkState?: unknown;
  children?: ReactNode;
}

export default function CommandDashboardTile({
  tile,
  onRemove,
  columns = FULL_WIDTH_TILE_COLUMNS,
  onDragStart,
  onDragEnd,
  onRefresh: onRefreshProp,
  isRefreshing: isRefreshingProp = false,
  refreshDisabledReason: refreshDisabledReasonProp,
  linkState: linkStateProp,
  children,
}: CommandDashboardTileProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [registeredRefresh, setRefresh] =
    useState<TileRefreshRegistration | null>(null);
  // A tile body only needs this to be read at click time (see getLinkState below), never
  // rendered, so it's a ref rather than state - state would force this shell to re-render every
  // time the body re-renders with a new linkState object (eg. a fresh literal on every render).
  const linkStateRef = useRef<unknown>(undefined);
  const setLinkState = useCallback((value: unknown) => {
    linkStateRef.current = value;
  }, []);
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const size = tileSizeForColumns(tile, columns);
  const draggable = Boolean(onDragStart);
  const onRefresh = onRefreshProp ?? registeredRefresh?.onRefresh;
  const isRefreshing =
    isRefreshingProp || Boolean(registeredRefresh?.isRefreshing);
  const refreshDisabledReason =
    refreshDisabledReasonProp ?? registeredRefresh?.refreshDisabledReason;
  const refreshDisabled = isRefreshing || Boolean(refreshDisabledReason);
  // Same missing-permission reason as Refresh, but not the in-flight spinner: navigating away
  // is still valid while a refresh is running.
  const viewAllDisabled = Boolean(refreshDisabledReason);
  const canNavigateToLink = Boolean(tile.link) && !viewAllDisabled;
  const getLinkState = () => linkStateProp ?? linkStateRef.current;

  const navigateToTileLink = () =>
    navigate(`/${tile.link}`, { state: getLinkState() });

  /**
   * Only the header is the drag handle, so the browser would otherwise drag a
   * picture of that thin strip. Handing it the card shows what is actually
   * being moved.
   */
  const handleDragStart = (e: DragEvent<HTMLElement>) => {
    const card = cardRef.current;
    // setDragImage is absent in jsdom, and the drag works without it.
    if (card && typeof e.dataTransfer.setDragImage === 'function') {
      const rect = card.getBoundingClientRect();
      e.dataTransfer.setDragImage(
        card,
        e.clientX - rect.left,
        e.clientY - rect.top
      );
    }
    e.dataTransfer.setData(TILE_DRAG_MIME_TYPE, tile.key);
    e.dataTransfer.effectAllowed = 'move';
    onDragStart?.();
  };

  const closeMenuThen = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuAnchor(null);
    action();
  };

  const refreshMenuItem = onRefresh && (
    <MenuItem
      aria-disabled={refreshDisabled || undefined}
      onClick={refreshDisabled ? undefined : closeMenuThen(onRefresh)}
      sx={{ opacity: refreshDisabled ? 0.5 : 1 }}
    >
      <ListItemIcon>
        {isRefreshing ? (
          <CircularProgress size={20} />
        ) : (
          <Refresh fontSize="small" />
        )}
      </ListItemIcon>
      Refresh
    </MenuItem>
  );

  const viewAllMenuItem = (
    <MenuItem
      aria-disabled={viewAllDisabled || undefined}
      onClick={viewAllDisabled ? undefined : closeMenuThen(navigateToTileLink)}
      sx={{ opacity: viewAllDisabled ? 0.5 : 1 }}
    >
      <ListItemIcon>
        <VisibilityOutlined fontSize="small" />
      </ListItemIcon>
      View All Activities
    </MenuItem>
  );

  return (
    <Card
      ref={cardRef}
      sx={{
        width: '100%',
        // The grid gives a row the tallest of its tiles' size heights, and
        // every card fills that row, so tiles sharing one end on the same
        // edge. The height for this tile's own span is the floor underneath
        // that, which is also what the card is outside the grid.
        height: '100%',
        minHeight: size.height,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        // Light mode reads its boundary from a drop shadow (matching Figma); dark mode swaps to a
        // border instead, since a shadow barely reads against a dark page background.
        border: (theme) =>
          theme.palette.mode === 'dark'
            ? `0.5px solid ${theme.palette.semantic.outline.light}`
            : 'none',
        // Same depth as the toolbar's Add Tiles box (command-dashboard-page.tsx) for a consistent
        // shadow across every "card" surface on the dashboard.
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? 'none'
            : '0px 1px 4px 0px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box
        sx={{
          // Fills the card so the body below can divide up exactly the height
          // the tile's size calls for. The zero minimums keep that to the
          // card's own size rather than however big its content wants to be.
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            height: '72px',
            flexShrink: 0,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            px: '16px',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            {...(draggable
              ? {
                  [TILE_DRAG_HANDLE_ATTRIBUTE]: true,
                  draggable: true,
                  onDragStart: handleDragStart,
                  onDragEnd,
                }
              : {})}
            sx={{
              width: '100%',
              cursor: draggable ? 'grab' : 'default',
              '&:active': { cursor: draggable ? 'grabbing' : 'default' },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TileIconBadge tile={tile} />
              <Stack>
                <Typography
                  variant="h6"
                  sx={{ color: (theme) => theme.palette.semantic.text.title2 }}
                >
                  {tile.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: (theme) => theme.palette.semantic.text.support }}
                >
                  {tile.description}
                </Typography>
              </Stack>
            </Stack>
            <IconButton
              size="small"
              aria-label={`${tile.title} tile actions`}
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    border: (theme) =>
                      `0.5px solid ${theme.palette.semantic.outline.light}`,
                  },
                },
              }}
            >
              {refreshMenuItem &&
                (refreshDisabledReason ? (
                  <Tooltip title={refreshDisabledReason} arrow>
                    {refreshMenuItem}
                  </Tooltip>
                ) : (
                  refreshMenuItem
                ))}
              <MenuItem onClick={closeMenuThen(() => onRemove(tile.key))}>
                <ListItemIcon>
                  <RemoveCircleOutline fontSize="small" />
                </ListItemIcon>
                Remove This Tile
              </MenuItem>
              {tile.link &&
                (viewAllDisabled ? (
                  <Tooltip title={refreshDisabledReason} arrow>
                    {viewAllMenuItem}
                  </Tooltip>
                ) : (
                  viewAllMenuItem
                ))}
            </Menu>
          </Stack>
        </Box>
        <TileRefreshContext value={setRefresh}>
          <TileLinkStateContext value={setLinkState}>
            {children && (
              <>
                <Divider sx={{ flexShrink: 0 }} />
                <Box
                  role={canNavigateToLink ? 'link' : undefined}
                  tabIndex={canNavigateToLink ? 0 : undefined}
                  aria-label={
                    canNavigateToLink
                      ? `View all ${tile.title} activities`
                      : undefined
                  }
                  onClick={canNavigateToLink ? navigateToTileLink : undefined}
                  onKeyDown={
                    canNavigateToLink
                      ? (e) => {
                          if (e.key === 'Enter') {
                            navigateToTileLink();
                          }
                        }
                      : undefined
                  }
                  sx={{
                    cursor: canNavigateToLink ? 'pointer' : 'default',
                    // The body gets whatever the header leaves of the tile's
                    // height and keeps anything longer to itself, so a tile is
                    // the size its design calls for rather than its content's.
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {children}
                </Box>
              </>
            )}
          </TileLinkStateContext>
        </TileRefreshContext>
      </Box>
    </Card>
  );
}
